import React, { useEffect } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
} from '@mui/material';
import { Field, Form, Formik } from 'formik';
import { TextField } from 'formik-mui';
import { formatQueryError } from 'renderer/shared/formatters/formatters';
import { toValidator } from 'renderer/shared/schemas/common.schemas';
import { AddCustomerFormSchema } from '../schemas/users.schemas';
import { useAddCustomerMutation } from '../services/users.endpoints';

const initialForm = {
  firstName: '',
  lastName: '',
  email: '',
  organization: '',
  password: '',
  confirmPassword: '',
};

interface AddCustomerDialogProps {
  open: boolean;
  onClose: () => void;
  onFeedback: (feedback: {
    severity: 'success' | 'error';
    message: string;
  }) => void;
}

const AddCustomerDialog: React.FC<AddCustomerDialogProps> = ({
  open,
  onClose,
  onFeedback,
}) => {
  const [addCustomer, { isLoading, error, isSuccess, data, reset }] =
    useAddCustomerMutation();

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open, reset]);

  useEffect(() => {
    if (isSuccess) {
      const customerName = [data?.user?.firstName, data?.user?.lastName]
        .filter(Boolean)
        .join(' ');
      const email = data?.user?.email;
      const namePart = customerName
        ? `Customer ${customerName} was created successfully.`
        : 'Customer was created successfully.';
      const emailPart = email ? ` An email has been sent to ${email}.` : '';
      onFeedback({
        severity: 'success',
        message: `${namePart}${emailPart}`,
      });
      onClose();
    }
  }, [data, isSuccess, onClose, onFeedback]);

  useEffect(() => {
    if (open && error) {
      onFeedback({
        severity: 'error',
        message: formatQueryError(error),
      });
    }
  }, [open, error, onFeedback]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Customer</DialogTitle>
      <Formik
        key={open ? 'add-customer-open' : 'add-customer-closed'}
        initialValues={initialForm}
        validate={toValidator(AddCustomerFormSchema)}
        onSubmit={(values) => addCustomer(values)}
      >
        <Form>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Field
                  component={TextField}
                  size="small"
                  name="firstName"
                  label="First Name"
                  disabled={isLoading}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field
                  component={TextField}
                  size="small"
                  name="lastName"
                  label="Last Name"
                  disabled={isLoading}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <Field
                  component={TextField}
                  size="small"
                  name="email"
                  type="email"
                  label="Email"
                  disabled={isLoading}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <Field
                  component={TextField}
                  size="small"
                  name="organization"
                  label="Organization"
                  disabled={isLoading}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <Field
                  component={TextField}
                  size="small"
                  name="password"
                  type="password"
                  label="Password"
                  disabled={isLoading}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <Field
                  component={TextField}
                  size="small"
                  name="confirmPassword"
                  type="password"
                  label="Confirm Password"
                  disabled={isLoading}
                  required
                  fullWidth
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={isLoading}>
              {isLoading ? <CircularProgress size={24} /> : 'Add Customer'}
            </Button>
          </DialogActions>
        </Form>
      </Formik>
    </Dialog>
  );
};

export default AddCustomerDialog;
